import { Component, ElementRef, Input, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { BaseComponent } from '../../base.component';

import { Orientation } from './splitter-types';

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-splitter-ng',
    templateUrl: './splitter.component.html',
    styleUrls: ['../../sharedStyles/base.scss', './splitter.component.scss']
})
export class SplitterComponent extends BaseComponent {
    private _leftWidth: string = null;
    private _orientation: string = Orientation.Horiztonal;
    private _topHeight: string = null;

    isResizing: boolean = false;
    previousMousePosition: any = null;

    @ViewChild('panelFirst') panelFirst: ElementRef<HTMLElement>;
    @ViewChild('panelLast') panelLast: ElementRef<HTMLElement>;
    @ViewChild('resizeContainer') resizeContainer: ElementRef<HTMLElement>;
    @ViewChild('resizeHandleHorizontal') resizeHandleHorizontal: ElementRef<HTMLElement>;
    @ViewChild('resizeHandleVertical') resizeHandleVertical: ElementRef<HTMLElement>;

    @Input()
    set leftWidth(value: string) {
        this._leftWidth = value;
    }
    get leftWidth(): string {
        return this._leftWidth;
    }

    @Input()
    set orientation(value: string) {
        this._orientation = value;
    }
    get orientation(): string {
        return this._orientation;
    }

    get panelFirstSize(): string {
        let panelAdjustSize = '100%';

        if (this.orientation === Orientation.Horiztonal) {
            panelAdjustSize = this.leftWidth ? this.leftWidth : '50%';
        } else {
            panelAdjustSize = this.topHeight ? this.topHeight : '50%';
        }

        return panelAdjustSize;
    }

    get panelLastSize(): string {
        let panelLastSize = '100%';

        if (this.orientation === Orientation.Horiztonal) {
            panelLastSize = this.leftWidth ? `${100 - Number(this.leftWidth.replace('%', ''))}%` : '50%';
        } else {
            panelLastSize = this.topHeight ? `${100 - Number(this.topHeight.replace('%', ''))}%` : '50%';
        }

        return panelLastSize;
    }

    @Input()
    set topHeight(value: string) {
        this._topHeight = value;
    }
    get topHeight(): string {
        return this._topHeight;
    }

    constructor(_vcr: ViewContainerRef) { super(_vcr); }

    private getMouseEventDetails(mouseEvent: MouseEvent) {
        return {
            clientX: mouseEvent.clientX,
            clientY: mouseEvent.clientY
        };
    }

    resize(mousePressEvent: MouseEvent) {
        this.isResizing = true;
        this.previousMousePosition = {
            clientX: mousePressEvent.clientX,
            clientY: mousePressEvent.clientY
        };
        const resizeContainerBoundingBox = this.resizeContainer.nativeElement.getBoundingClientRect();

        const onMouseMove = (mouseMoveEvent: MouseEvent) => {
            if (this.orientation === Orientation.Horiztonal && mouseMoveEvent.clientX !== this.previousMousePosition.clientX) {
                const mousePositionOffset = mouseMoveEvent.clientX - resizeContainerBoundingBox.left;
                const resizeHandleHorizontalBoundingBox = this.resizeHandleHorizontal.nativeElement.getBoundingClientRect();
                let resizeHandleHorizontalOffset;
                let isResizeInsideContainer;
                let newPosition;

                if (mouseMoveEvent.clientX < this.previousMousePosition.clientX) {
                    resizeHandleHorizontalOffset = resizeHandleHorizontalBoundingBox.left - resizeContainerBoundingBox.left;
                    isResizeInsideContainer = resizeContainerBoundingBox.left < resizeHandleHorizontalBoundingBox.left;
                    newPosition = Math.max(0, Math.min(mousePositionOffset, resizeHandleHorizontalOffset));
                } else {
                    resizeHandleHorizontalOffset = resizeHandleHorizontalBoundingBox.right - resizeContainerBoundingBox.right;
                    isResizeInsideContainer = resizeContainerBoundingBox.right > resizeHandleHorizontalBoundingBox.right;
                    newPosition = Math.min(resizeContainerBoundingBox.width - resizeHandleHorizontalBoundingBox.width, Math.max(mousePositionOffset, resizeHandleHorizontalOffset));
                }

                if (isResizeInsideContainer) {
                    this.panelFirst.nativeElement.style.flexBasis = `${newPosition}px`;
                    this.panelLast.nativeElement.style.flexBasis = `${resizeContainerBoundingBox.width - newPosition}px`;
                    this.previousMousePosition = this.getMouseEventDetails(mouseMoveEvent);
                }
            } else if (this.orientation === Orientation.Vertical && mouseMoveEvent.clientY !== this.previousMousePosition.clientY) {
                const mousePositionOffset = mouseMoveEvent.clientY - resizeContainerBoundingBox.top;
                const resizeHandleVerticalBoundingBox = this.resizeHandleVertical.nativeElement.getBoundingClientRect();
                let resizeHandleVerticalOffset;
                let isResizeInsideContainer;
                let newPosition;

                if (mouseMoveEvent.clientY < this.previousMousePosition.clientY) {
                    resizeHandleVerticalOffset = resizeHandleVerticalBoundingBox.top - resizeContainerBoundingBox.top;
                    isResizeInsideContainer = resizeContainerBoundingBox.top < resizeHandleVerticalBoundingBox.top;
                    newPosition = Math.max(0, Math.min(mousePositionOffset, resizeHandleVerticalOffset));
                } else {
                    resizeHandleVerticalOffset = resizeHandleVerticalBoundingBox.bottom - resizeContainerBoundingBox.bottom;
                    isResizeInsideContainer = resizeContainerBoundingBox.bottom > resizeHandleVerticalBoundingBox.bottom;
                    newPosition = Math.min(resizeContainerBoundingBox.height - resizeHandleVerticalBoundingBox.height, Math.max(mousePositionOffset, resizeHandleVerticalOffset));
                }

                if (isResizeInsideContainer) {
                    this.panelFirst.nativeElement.style.flexBasis = `${newPosition}px`;
                    this.panelLast.nativeElement.style.flexBasis = `${resizeContainerBoundingBox.height - newPosition}px`;
                    this.previousMousePosition = this.getMouseEventDetails(mouseMoveEvent);
                }
            }
        };

        const onMouseUp = () => {
            this.isResizing = false;
            this.previousMousePosition = null;

            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    }
}
